import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import DatePicker from "react-datepicker";
import { Link2, Lock, Mail, MapPin, Plus, Smartphone, Trash2, Upload, User } from "lucide-react";
import ApplicantShell from "../components/ApplicantShell";
import { SocialBrandIcon, socialUrlForLink } from "../components/ProfileSocialBrand";
import {
  COUNTRY_DIAL_OPTIONS,
  getCountryIsoFromDial,
  getDialCodeForCountryIso,
} from "../countryDialOptions";
import profilePlaceholder from "../assets/profile-placeholder.svg";
import {
  deleteApplicantCv,
  deleteApplicantProfilePhoto,
  fetchApplicantWorkEvidenceBlob,
  isApiConfigured,
  patchApplicantProfile,
  postApplicantCv,
  postApplicantProfilePhoto,
  postApplicantWorkEvidence,
  postRequestEmailChange,
  getEmailChangeLinkRequestEligibility,
  postRequestMobileChange,
  getMobileChangeLinkRequestEligibility,
} from "../apiClient";
import { PROFILE_PHOTO_MAX_BYTES, PROFILE_PHOTO_MAX_MB } from "../config";
import { COUNTRIES } from "../data/countriesList";
import { APPLICANT_COUNTRY_GROUPS } from "../data/applicantCountryGroups";
import WorkCountriesPillPicker from "../components/WorkCountriesPillPicker";

const MAX_CUSTOM_SOCIAL_LINKS = 4;
const MAX_WORK_COUNTRIES = 3;
const MAX_COUNTRY_CODES_PER_ROW = 50;

const ISO_SET = new Set(COUNTRIES.map((c) => c.value));

function formatLocaleDateTime(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return String(iso);
  }
}

function makeRowId() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `wc-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

function emptyCountryRow() {
  return {
    id: makeRowId(),
    countryCodes: [],
    countriesInputDraft: "",
    proofFileName: "",
    clearProof: false,
  };
}

function codesIntersection(a, b) {
  const sb = new Set(b);
  return a.filter((x) => sb.has(x));
}

function shouldClearProofWhenCodesChange(prevCodes, newCodes) {
  if (prevCodes.length === 0) return false;
  if (newCodes.length === 0) return false;
  return codesIntersection(prevCodes, newCodes).length === 0;
}

function emptyCustomSocialRow() {
  return { id: makeRowId(), platformName: "", url: "" };
}

function stripHttpScheme(raw) {
  return String(raw || "").replace(/^\s*https?:\/\//i, "").trimStart();
}

/** Parse `YYYY-MM-DD` as local calendar date (avoids UTC off-by-one). */
function parseYyyyMmDdLocal(s) {
  if (!s || typeof s !== "string") return null;
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return null;
  const y = Number(m[1]);
  const mo = Number(m[2]) - 1;
  const d = Number(m[3]);
  const dt = new Date(y, mo, d);
  if (dt.getFullYear() !== y || dt.getMonth() !== mo || dt.getDate() !== d) return null;
  return dt;
}

function toYyyyMmDdLocal(date) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "";
  const y = date.getFullYear();
  const mo = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${mo}-${d}`;
}

function guessCountryCodeFromLegacyText(text) {
  if (!text || !String(text).trim()) return "";
  const t = String(text).trim().toLowerCase();
  for (const opt of COUNTRY_DIAL_OPTIONS) {
    const namePart = opt.label.split("(")[0].trim().toLowerCase().replace(/^the\s+/, "");
    if (namePart.length >= 4 && (t.includes(namePart) || namePart.includes(t))) {
      return opt.value;
    }
  }
  if (/(^|[^a-z])uk([^a-z]|$)|england|scotland|wales|britain/.test(t)) return "GB";
  if (t.includes("ireland") && !t.includes("northern")) return "IE";
  if (t.includes("uae") || t.includes("emirates") || t.includes("dubai") || t.includes("abu dhabi")) return "AE";
  if (t.includes("india")) return "IN";
  if (t.includes("australia")) return "AU";
  if (t.includes("united states") || /\busa\b|\bus\b/.test(t)) return "US";
  return "";
}

function normalizeSocialBasicsFromUser(user) {
  const s = user?.socialProfiles;
  if (!s || typeof s !== "object") {
    return { linkedin: "", instagram: "" };
  }
  return {
    linkedin: stripHttpScheme(typeof s.linkedin === "string" ? s.linkedin : ""),
    instagram: stripHttpScheme(typeof s.instagram === "string" ? s.instagram : ""),
  };
}

function normalizeCustomSocialRowsFromUser(user) {
  const s = user?.socialProfiles;
  if (!s || typeof s !== "object") return [];
  if (Array.isArray(s.customs)) {
    return s.customs.slice(0, MAX_CUSTOM_SOCIAL_LINKS).map((row) => ({
      id: typeof row?.id === "string" && row.id ? row.id : makeRowId(),
      platformName: String(row?.platformName ?? row?.name ?? "").trim(),
      url: stripHttpScheme(String(row?.url ?? row?.link ?? "").trim()),
    }));
  }
  const legacyLabel = typeof s.customLabel === "string" ? s.customLabel.trim() : "";
  const legacyUrl = stripHttpScheme(typeof s.customUrl === "string" ? s.customUrl.trim() : "");
  if (legacyLabel || legacyUrl) {
    return [{ id: "legacy-custom", platformName: legacyLabel, url: legacyUrl }];
  }
  return [];
}

function normalizeWorkCountryIso(raw, legacyCountryText) {
  const s = typeof raw === "string" ? raw.trim() : "";
  if (/^[A-Za-z]{2}$/.test(s)) return s.toUpperCase();
  if (s) {
    const fromDial = getCountryIsoFromDial(s);
    if (fromDial) return fromDial;
  }
  return guessCountryCodeFromLegacyText(legacyCountryText) || "";
}

function mapApiWorkCountryRowToState(row, i) {
  let codes = [];
  if (Array.isArray(row.countryCodes) && row.countryCodes.length) {
    codes = row.countryCodes
      .map((c) => String(c || "").trim().toUpperCase())
      .filter((c) => ISO_SET.has(c));
  } else {
    const one = normalizeWorkCountryIso(row.countryCode, row.country);
    if (one && ISO_SET.has(one)) codes = [one];
  }
  return {
    id: row.id || `wc-${i}`,
    countryCodes: codes,
    countriesInputDraft: "",
    proofFileName: typeof row.proofFileName === "string" ? row.proofFileName : "",
    clearProof: false,
  };
}

function normalizeWorkCountriesFromUser(user) {
  const arr = user?.workCountries;
  if (Array.isArray(arr) && arr.length > 0) {
    return arr.slice(0, MAX_WORK_COUNTRIES).map((row, i) => mapApiWorkCountryRowToState(row, i));
  }
  const legacy = user?.locations;
  if (typeof legacy === "string" && legacy.trim()) {
    return legacy
      .split(/\n/)
      .map((line) => line.trim())
      .filter(Boolean)
      .slice(0, MAX_WORK_COUNTRIES)
      .map((line, i) => ({
        id: `migrated-${i}`,
        countryCodes: [guessCountryCodeFromLegacyText(line)].filter((c) => c && ISO_SET.has(c)),
        countriesInputDraft: "",
        proofFileName: "",
        clearProof: false,
      }));
  }
  return [emptyCountryRow()];
}

function isWorkEvidenceUploadFile(file) {
  if (!file || typeof file !== "object") return false;
  const name = String(file.name || "");
  if (!/\.(pdf|png|jpg|jpeg|webp)$/i.test(name)) return false;
  const t = String(file.type || "").toLowerCase();
  if (
    t === "application/pdf" ||
    t === "image/png" ||
    t === "image/jpeg" ||
    t === "image/webp"
  ) {
    return true;
  }
  if (!t) return true;
  return t.startsWith("image/");
}

function sanitizeProofDownloadFileName(name) {
  const base = String(name || "").split(/[/\\]/).pop() || "";
  const cleaned = base.replace(/[^\w.\- ()]+/g, "_").trim();
  if (cleaned) return cleaned.length > 200 ? cleaned.slice(0, 200) : cleaned;
  return "proof";
}

/** Save a Blob (or File) in the browser using the given filename hint. */
function triggerBrowserDownload(blob, suggestedName) {
  const filename = sanitizeProofDownloadFileName(suggestedName);
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.rel = "noopener noreferrer";
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 120_000);
}

function buildWorkCountriesPatchPayload(rows) {
  return rows
    .slice(0, MAX_WORK_COUNTRIES)
    .map((row) => {
      const countryCodes = [
        ...new Set(
          (row.countryCodes || [])
            .map((c) => String(c || "").trim().toUpperCase())
            .filter((c) => ISO_SET.has(c))
        ),
      ].slice(0, MAX_COUNTRY_CODES_PER_ROW);
      return {
        id: row.id,
        countryCodes,
        ...(row.clearProof ? { clearProof: true } : {}),
      };
    })
    .filter((row) => row.countryCodes.length > 0 || row.clearProof);
}

/** Stable JSON for dirty detection (manage profile vs last-known `currentUser`). */
function fingerprintApplicantManageForm({
  name,
  email,
  mobileCountryIso,
  mobile,
  dateOfBirth,
  residenceCountryIso,
  townCityOfResidence,
  workCountries,
  socialLinkedin,
  socialInstagram,
  customSocialRows,
  photoPreview,
  cvFileName,
}) {
  const savedCountries = workCountries
    .slice(0, MAX_WORK_COUNTRIES)
    .map(({ id, countryCodes, countriesInputDraft, proofFileName, clearProof }) => ({
      id,
      countryCodes: [...(countryCodes || [])].filter((c) => ISO_SET.has(c)).sort().join(","),
      countriesInputDraft: String(countriesInputDraft || "").trim(),
      proofFileName: (proofFileName || "").trim(),
      clearProof: Boolean(clearProof),
    }))
    .filter((row) => row.countryCodes || row.countriesInputDraft || row.proofFileName || row.clearProof);

  const customs = customSocialRows
    .slice(0, MAX_CUSTOM_SOCIAL_LINKS)
    .map(({ id, platformName, url }) => ({
      id,
      platformName: (platformName || "").trim(),
      url: (url || "").trim(),
    }))
    .filter((row) => row.platformName || row.url);

  const socialProfiles = {
    linkedin: socialLinkedin.trim(),
    instagram: socialInstagram.trim(),
    youtube: "",
    customs,
  };

  return JSON.stringify({
    name: name.trim(),
    email: email.trim(),
    countryCode: getDialCodeForCountryIso(mobileCountryIso),
    mobile: mobile.trim(),
    dateOfBirth: dateOfBirth.trim(),
    residenceCountryIso: String(residenceCountryIso || "").trim().toUpperCase(),
    townCityOfResidence: String(townCityOfResidence || "").trim(),
    workCountries: savedCountries,
    socialProfiles,
    photo: (photoPreview || "").trim(),
    cv: (cvFileName || "").trim(),
  });
}

function fingerprintFromCurrentUser(user) {
  if (!user) return "";
  const mobileCountryIso = getCountryIsoFromDial(user.countryCode) || "GB";
  const basics = normalizeSocialBasicsFromUser(user);
  const resIso = String(user.countryOfResidence || "").trim().toUpperCase();
  return fingerprintApplicantManageForm({
    name: user.name || "",
    email: user.email || "",
    mobileCountryIso,
    mobile: user.mobile || "",
    dateOfBirth: user.dateOfBirth || "",
    residenceCountryIso: ISO_SET.has(resIso) ? resIso : "",
    townCityOfResidence: user.townCityOfResidence || "",
    workCountries: normalizeWorkCountriesFromUser(user),
    socialLinkedin: basics.linkedin,
    socialInstagram: basics.instagram,
    customSocialRows: normalizeCustomSocialRowsFromUser(user),
    photoPreview: user.photoDataUrl || "",
    cvFileName: user.cvFileName || "",
  });
}

function ApplicantProfilePage({ currentUser, setCurrentUser, authToken }) {
  const navigate = useNavigate();
  const photoInputRef = useRef(null);
  const pendingPhotoFileRef = useRef(null);
  const pendingCvFileRef = useRef(null);
  const cvInputRef = useRef(null);
  const proofInputRefs = useRef({});
  /** @type {React.MutableRefObject<Record<string, File>>} */
  const pendingWorkEvidenceByRowIdRef = useRef({});

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [mobileCountryIso, setMobileCountryIso] = useState("GB");
  const [mobile, setMobile] = useState("");
  const [dateOfBirth, setDateOfBirth] = useState("");
  const dobSelected = useMemo(() => parseYyyyMmDdLocal(dateOfBirth), [dateOfBirth]);
  const [residenceCountryIso, setResidenceCountryIso] = useState("");
  const [townCityOfResidence, setTownCityOfResidence] = useState("");
  const [workCountries, setWorkCountries] = useState([emptyCountryRow()]);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [cvFileName, setCvFileName] = useState("");
  const [pendingFilePickNonce, setPendingFilePickNonce] = useState(0);
  const [emailChangeBusy, setEmailChangeBusy] = useState(false);
  const [emailChangeEligibility, setEmailChangeEligibility] = useState(null);
  const [mobileChangeBusy, setMobileChangeBusy] = useState(false);
  const [mobileChangeEligibility, setMobileChangeEligibility] = useState(null);
  const [socialLinkedin, setSocialLinkedin] = useState("");
  const [socialInstagram, setSocialInstagram] = useState("");
  const [customSocialRows, setCustomSocialRows] = useState([]);
  const [toast, setToast] = useState(null);
  const toastTimerRef = useRef(null);

  useEffect(() => {
    return () => {
      if (toastTimerRef.current) {
        window.clearTimeout(toastTimerRef.current);
        toastTimerRef.current = null;
      }
    };
  }, []);

  const dismissToast = useCallback(() => {
    if (toastTimerRef.current) {
      window.clearTimeout(toastTimerRef.current);
      toastTimerRef.current = null;
    }
    setToast(null);
  }, []);

  const pushToast = useCallback(
    (message, tone = "success") => {
      if (toastTimerRef.current) {
        window.clearTimeout(toastTimerRef.current);
        toastTimerRef.current = null;
      }
      setToast({ message, tone });
      if (tone === "success") {
        toastTimerRef.current = window.setTimeout(() => {
          setToast(null);
          toastTimerRef.current = null;
        }, 5500);
      }
    },
    []
  );

  const loadEmailChangeEligibility = useCallback(async () => {
    if (!isApiConfigured() || !String(authToken || "").trim()) {
      setEmailChangeEligibility(null);
      return;
    }
    try {
      const data = await getEmailChangeLinkRequestEligibility(String(authToken).trim());
      setEmailChangeEligibility(data);
    } catch {
      setEmailChangeEligibility(null);
    }
  }, [authToken]);

  useEffect(() => {
    loadEmailChangeEligibility();
  }, [loadEmailChangeEligibility]);

  useEffect(() => {
    if (!emailChangeEligibility || emailChangeEligibility.canRequest) return undefined;
    const id = window.setInterval(() => {
      loadEmailChangeEligibility();
    }, 30000);
    return () => window.clearInterval(id);
  }, [emailChangeEligibility, loadEmailChangeEligibility]);

  const loadMobileChangeEligibility = useCallback(async () => {
    if (!isApiConfigured() || !String(authToken || "").trim()) {
      setMobileChangeEligibility(null);
      return;
    }
    try {
      const data = await getMobileChangeLinkRequestEligibility(String(authToken).trim());
      setMobileChangeEligibility(data);
    } catch {
      setMobileChangeEligibility(null);
    }
  }, [authToken]);

  useEffect(() => {
    loadMobileChangeEligibility();
  }, [loadMobileChangeEligibility]);

  useEffect(() => {
    if (!mobileChangeEligibility || mobileChangeEligibility.canRequest) return undefined;
    const id = window.setInterval(() => {
      loadMobileChangeEligibility();
    }, 30000);
    return () => window.clearInterval(id);
  }, [mobileChangeEligibility, loadMobileChangeEligibility]);

  const emailChangeHintBlock = useMemo(() => {
    if (!emailChangeEligibility || emailChangeEligibility.canRequest) return null;
    const support = (
      <a className="profile-manage-email-change-mailto" href="mailto:help@jobuwin.com">
        help@jobuwin.com
      </a>
    );
    if (emailChangeEligibility.lockedUntil) {
      return (
        <p className="profile-manage-email-change-hint">
          You have reached the maximum number of email change link requests. This option will be available again after{" "}
          <strong>{formatLocaleDateTime(emailChangeEligibility.lockedUntil)}</strong>. If you need more support, please email us at{" "}
          {support}.
        </p>
      );
    }
    if (emailChangeEligibility.cooldownEndsAt) {
      return (
        <p className="profile-manage-email-change-hint">
          We sent an email to your current email to initiate an email change. If you need more support, please email us at{" "}
          {support}. You can request another link after{" "}
          <strong>{formatLocaleDateTime(emailChangeEligibility.cooldownEndsAt)}</strong>.
        </p>
      );
    }
    return null;
  }, [emailChangeEligibility]);

  const mobileChangeHintBlock = useMemo(() => {
    if (!mobileChangeEligibility || mobileChangeEligibility.canRequest) return null;
    const support = (
      <a className="profile-manage-email-change-mailto" href="mailto:help@jobuwin.com">
        help@jobuwin.com
      </a>
    );
    if (mobileChangeEligibility.lockedUntil) {
      return (
        <p className="profile-manage-email-change-hint">
          You have reached the maximum number of mobile change link requests. This option will be available again after{" "}
          <strong>{formatLocaleDateTime(mobileChangeEligibility.lockedUntil)}</strong>. If you need more support, please
          email us at {support}.
        </p>
      );
    }
    if (mobileChangeEligibility.cooldownEndsAt) {
      return (
        <p className="profile-manage-email-change-hint">
          We sent an email to your current email to initiate a mobile change. If you need more support, please email us
          at {support}. You can request another link after{" "}
          <strong>{formatLocaleDateTime(mobileChangeEligibility.cooldownEndsAt)}</strong>.
        </p>
      );
    }
    return null;
  }, [mobileChangeEligibility]);

  const handleRequestEmailChange = useCallback(async () => {
    if (!isApiConfigured()) {
      pushToast("Connect the app to the API to change your email.", "error");
      return;
    }
    const t = String(authToken || "").trim();
    if (!t) {
      pushToast("Sign in again to change your email.", "error");
      return;
    }
    setEmailChangeBusy(true);
    try {
      const data = await postRequestEmailChange(t);
      if (data.skipped) {
        pushToast(data.message || "Email could not be sent.", "error");
      } else if (data.sent) {
        if (data.eligibility) {
          setEmailChangeEligibility(data.eligibility);
        } else {
          await loadEmailChangeEligibility();
        }
      } else {
        pushToast(data.message || "We could not send the email. Try again shortly.", "error");
      }
    } catch (err) {
      pushToast(err instanceof Error ? err.message : "Request failed", "error");
      await loadEmailChangeEligibility();
    } finally {
      setEmailChangeBusy(false);
    }
  }, [authToken, pushToast, loadEmailChangeEligibility]);

  const handleRequestMobileChange = useCallback(async () => {
    if (!isApiConfigured()) {
      pushToast("Connect the app to the API to change your mobile number.", "error");
      return;
    }
    const t = String(authToken || "").trim();
    if (!t) {
      pushToast("Sign in again to change your mobile number.", "error");
      return;
    }
    setMobileChangeBusy(true);
    try {
      const data = await postRequestMobileChange(t);
      if (data.skipped) {
        pushToast(data.message || "Email could not be sent.", "error");
      } else if (data.sent) {
        if (data.eligibility) {
          setMobileChangeEligibility(data.eligibility);
        } else {
          await loadMobileChangeEligibility();
        }
        pushToast("Check your email for the link to continue.", "success");
      } else {
        pushToast(data.message || "We could not send the email. Try again shortly.", "error");
      }
    } catch (err) {
      pushToast(err instanceof Error ? err.message : "Request failed", "error");
      await loadMobileChangeEligibility();
    } finally {
      setMobileChangeBusy(false);
    }
  }, [authToken, pushToast, loadMobileChangeEligibility]);

  const isProfileDirty = useMemo(() => {
    if (!currentUser) return false;
    if (pendingPhotoFileRef.current || pendingCvFileRef.current) return true;
    const pendingWc = pendingWorkEvidenceByRowIdRef.current;
    if (Object.keys(pendingWc).some((k) => pendingWc[k])) return true;
    const baseline = fingerprintFromCurrentUser(currentUser);
    const now = fingerprintApplicantManageForm({
      name,
      email,
      mobileCountryIso,
      mobile,
      dateOfBirth,
      residenceCountryIso,
      townCityOfResidence,
      workCountries,
      socialLinkedin,
      socialInstagram,
      customSocialRows,
      photoPreview: photoPreview || "",
      cvFileName,
    });
    return now !== baseline;
  }, [
    currentUser,
    name,
    email,
    mobileCountryIso,
    mobile,
    dateOfBirth,
    residenceCountryIso,
    townCityOfResidence,
    workCountries,
    socialLinkedin,
    socialInstagram,
    customSocialRows,
    photoPreview,
    cvFileName,
    pendingFilePickNonce,
  ]);

  const resetProfileFormToSaved = useCallback(() => {
    if (!currentUser) return;
    pendingPhotoFileRef.current = null;
    pendingCvFileRef.current = null;
    pendingWorkEvidenceByRowIdRef.current = {};
    if (photoInputRef.current) photoInputRef.current.value = "";
    if (cvInputRef.current) cvInputRef.current.value = "";
    for (const el of Object.values(proofInputRefs.current)) {
      if (el && typeof el.value === "string") el.value = "";
    }
    setPendingFilePickNonce((n) => n + 1);
    setName(currentUser.name || "");
    setEmail(currentUser.email || "");
    setMobileCountryIso(getCountryIsoFromDial(currentUser.countryCode) || "GB");
    setMobile(currentUser.mobile || "");
    setDateOfBirth(currentUser.dateOfBirth || "");
    {
      const r = String(currentUser.countryOfResidence || "").trim().toUpperCase();
      setResidenceCountryIso(ISO_SET.has(r) ? r : "");
    }
    setTownCityOfResidence(
      typeof currentUser.townCityOfResidence === "string" ? currentUser.townCityOfResidence : ""
    );
    setWorkCountries(normalizeWorkCountriesFromUser(currentUser));
    setPhotoPreview(currentUser.photoDataUrl || null);
    setCvFileName(currentUser.cvFileName || "");
    const basics = normalizeSocialBasicsFromUser(currentUser);
    setSocialLinkedin(basics.linkedin);
    setSocialInstagram(basics.instagram);
    setCustomSocialRows(normalizeCustomSocialRowsFromUser(currentUser));
  }, [currentUser]);

  useEffect(() => {
    if (!currentUser) return;
    setName(currentUser.name || "");
    setEmail(currentUser.email || "");
    setMobileCountryIso(getCountryIsoFromDial(currentUser.countryCode) || "GB");
    setMobile(currentUser.mobile || "");
    setDateOfBirth(currentUser.dateOfBirth || "");
    {
      const r = String(currentUser.countryOfResidence || "").trim().toUpperCase();
      setResidenceCountryIso(ISO_SET.has(r) ? r : "");
    }
    setTownCityOfResidence(
      typeof currentUser.townCityOfResidence === "string" ? currentUser.townCityOfResidence : ""
    );
    setWorkCountries(normalizeWorkCountriesFromUser(currentUser));
    setPhotoPreview(currentUser.photoDataUrl || null);
    setCvFileName(currentUser.cvFileName || "");
    const basics = normalizeSocialBasicsFromUser(currentUser);
    setSocialLinkedin(basics.linkedin);
    setSocialInstagram(basics.instagram);
    setCustomSocialRows(normalizeCustomSocialRowsFromUser(currentUser));
  }, [currentUser]);

  const onPhotoChange = useCallback(
    (event) => {
      const file = event.target.files?.[0];
      if (!file || !file.type.startsWith("image/")) return;
      if (file.size > PROFILE_PHOTO_MAX_BYTES) {
        pushToast(`Image is too large (max ${PROFILE_PHOTO_MAX_MB} MB).`, "error");
        if (photoInputRef.current) photoInputRef.current.value = "";
        return;
      }
      pendingPhotoFileRef.current = file;
      setPendingFilePickNonce((n) => n + 1);
      const reader = new FileReader();
      reader.onload = () => {
        setPhotoPreview(String(reader.result));
      };
      reader.readAsDataURL(file);
    },
    [pushToast]
  );

  const handleProfilePhotoDelete = useCallback(() => {
    if (!photoPreview) return;

    pendingPhotoFileRef.current = null;
    setPendingFilePickNonce((n) => n + 1);
    if (photoInputRef.current) photoInputRef.current.value = "";

    const persistedHttp =
      currentUser?.photoDataUrl && !String(currentUser.photoDataUrl).startsWith("data:");
    const previewIsData = String(photoPreview).startsWith("data:");

    if (previewIsData) {
      if (persistedHttp) {
        setPhotoPreview(currentUser.photoDataUrl);
      } else {
        setPhotoPreview(null);
      }
      return;
    }

    setPhotoPreview(null);
  }, [currentUser, photoPreview]);

  const onCvChange = useCallback(
    (event) => {
      const file = event.target.files?.[0];
      if (!file) return;
      const okType =
        file.type === "application/pdf" ||
        file.type === "application/msword" ||
        file.type === "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
      if (!okType) {
        pushToast("CV must be a PDF, DOC, or DOCX file.", "error");
        if (cvInputRef.current) cvInputRef.current.value = "";
        return;
      }
      if (file.size > PROFILE_PHOTO_MAX_BYTES) {
        pushToast(`CV is too large (max ${PROFILE_PHOTO_MAX_MB} MB).`, "error");
        if (cvInputRef.current) cvInputRef.current.value = "";
        return;
      }
      pendingCvFileRef.current = file;
      setPendingFilePickNonce((n) => n + 1);
      setCvFileName(file.name);
      if (cvInputRef.current) cvInputRef.current.value = "";
    },
    [pushToast]
  );

  const handleCvDelete = useCallback(() => {
    const hadPendingCv = !!pendingCvFileRef.current;
    const persistedName = (currentUser?.cvFileName || "").trim();
    pendingCvFileRef.current = null;
    if (cvInputRef.current) cvInputRef.current.value = "";
    setPendingFilePickNonce((n) => n + 1);
    if (hadPendingCv && persistedName) {
      setCvFileName(currentUser.cvFileName || "");
      return;
    }
    setCvFileName("");
  }, [currentUser]);

  /** Persist country row to DB then upload evidence (same order as full profile save). */
  const uploadWorkEvidenceAfterPick = useCallback(
    async (rowsSnapshot, rowId, file, token) => {
      try {
        await patchApplicantProfile(
          {
            dateOfBirth: dateOfBirth.trim(),
            workCountries: buildWorkCountriesPatchPayload(rowsSnapshot),
          },
          token
        );
        const { user: evUser } = await postApplicantWorkEvidence(file, rowId, token);
        delete pendingWorkEvidenceByRowIdRef.current[rowId];
        setPendingFilePickNonce((n) => n + 1);
        setCurrentUser((prev) => ({ ...prev, ...evUser }));
        setWorkCountries((prev) =>
          prev.map((r) => {
            if (String(r.id) !== String(rowId)) return r;
            const sr = (evUser.workCountries || []).find((x) => String(x.id) === String(rowId));
            const proofFileName =
              sr && typeof sr.proofFileName === "string" && sr.proofFileName.trim()
                ? sr.proofFileName.trim()
                : file.name;
            const nextRow = sr ? mapApiWorkCountryRowToState(sr, 0) : { ...r, proofFileName, clearProof: false };
            return { ...nextRow, proofFileName, clearProof: false };
          })
        );
        pushToast("Proof saved to your profile.", "success");
      } catch (err) {
        const msg = err instanceof Error ? err.message : "Could not upload proof.";
        pushToast(msg, "error");
      }
    },
    [dateOfBirth, pushToast, setCurrentUser]
  );

  const patchWorkCountryRowCodes = useCallback(
    (index, nextCodesFn, opts = {}) => {
      const { clearDraft = false } = opts;
      const token = String(authToken || "").trim();
      const canAutoUpload = isApiConfigured() && token && currentUser?.role === "applicant";

      setWorkCountries((rows) => {
        const next = [...rows];
        const prev = next[index];
        if (!prev) return rows;
        const prevCodes = [...(prev.countryCodes || [])].filter((c) => ISO_SET.has(c));
        const proposed = nextCodesFn(prevCodes);
        const sanitized = [...new Set(proposed.filter((c) => ISO_SET.has(c)))].slice(
          0,
          MAX_COUNTRY_CODES_PER_ROW
        );

        if (shouldClearProofWhenCodesChange(prevCodes, sanitized)) {
          delete pendingWorkEvidenceByRowIdRef.current[prev.id];
          setPendingFilePickNonce((n) => n + 1);
        }

        const clearProofAfterChange = shouldClearProofWhenCodesChange(prevCodes, sanitized);

        next[index] = {
          ...prev,
          countryCodes: sanitized,
          countriesInputDraft: clearDraft ? "" : prev.countriesInputDraft,
          proofFileName: clearProofAfterChange ? "" : prev.proofFileName,
          clearProof: clearProofAfterChange,
        };

        const pendingFile = pendingWorkEvidenceByRowIdRef.current[prev.id];
        const shouldAutoUpload = Boolean(
          canAutoUpload &&
            sanitized.length > 0 &&
            prevCodes.length === 0 &&
            pendingFile &&
            !clearProofAfterChange
        );
        if (shouldAutoUpload) {
          const rowId = prev.id;
          queueMicrotask(() => {
            void uploadWorkEvidenceAfterPick(next, rowId, pendingFile, token);
          });
        }

        return next;
      });
    },
    [authToken, currentUser?.role, uploadWorkEvidenceAfterPick]
  );

  const updateWorkCountryDraft = (index, draft) => {
    setWorkCountries((rows) =>
      rows.map((r, i) => (i === index ? { ...r, countriesInputDraft: draft } : r))
    );
  };

  const addWorkCountryCode = (index, iso) => {
    const code = String(iso || "").trim().toUpperCase();
    if (!ISO_SET.has(code)) return;
    patchWorkCountryRowCodes(
      index,
      (prev) => (prev.includes(code) ? prev : [...prev, code]),
      { clearDraft: true }
    );
  };

  const removeWorkCountryCode = (index, iso) => {
    patchWorkCountryRowCodes(index, (prev) => prev.filter((c) => c !== iso));
  };

  const addWorkCountryGroup = (index, groupCodes) => {
    const list = [
      ...new Set(
        (groupCodes || [])
          .map((c) => String(c || "").trim().toUpperCase())
          .filter((c) => ISO_SET.has(c))
      ),
    ];
    if (!list.length) return;
    patchWorkCountryRowCodes(
      index,
      (prev) => {
        const seen = new Set(prev);
        const out = [...prev];
        for (const c of list) {
          if (seen.has(c)) continue;
          if (out.length >= MAX_COUNTRY_CODES_PER_ROW) break;
          seen.add(c);
          out.push(c);
        }
        return out;
      },
      { clearDraft: true }
    );
  };

  const onProofChange = (index) => (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const input = event.target;
    if (!isWorkEvidenceUploadFile(file)) {
      pushToast("Proof must be a PDF or an image (PNG, JPEG, or WebP).", "error");
      input.value = "";
      return;
    }
    if (file.size > PROFILE_PHOTO_MAX_BYTES) {
      pushToast(`Proof file is too large (max ${PROFILE_PHOTO_MAX_MB} MB).`, "error");
      input.value = "";
      return;
    }
    const token = String(authToken || "").trim();
    const canAutoUpload =
      isApiConfigured() && token && currentUser?.role === "applicant";
    let rowsForUpload = null;
    let uploadRowId = "";
    setWorkCountries((rows) => {
      const row = rows[index];
      if (!row) return rows;
      const hasCountry = (row.countryCodes || []).length > 0;

      if (!hasCountry) {
        const next = [...rows];
        pendingWorkEvidenceByRowIdRef.current[row.id] = file;
        setPendingFilePickNonce((n) => n + 1);
        next[index] = { ...row, proofFileName: file.name, clearProof: false };
        return next;
      }

      const next = [...rows];
      pendingWorkEvidenceByRowIdRef.current[row.id] = file;
      setPendingFilePickNonce((n) => n + 1);
      next[index] = { ...next[index], proofFileName: file.name, clearProof: false };
      if (canAutoUpload) {
        rowsForUpload = next;
        uploadRowId = row.id;
      }
      return next;
    });
    if (canAutoUpload && rowsForUpload && uploadRowId) {
      queueMicrotask(() => {
        void uploadWorkEvidenceAfterPick(rowsForUpload, uploadRowId, file, token);
      });
    }
    input.value = "";
  };

  const addCountryRow = () => {
    setWorkCountries((rows) => {
      if (rows.length >= MAX_WORK_COUNTRIES) return rows;
      return [...rows, emptyCountryRow()];
    });
  };

  const removeCountryRow = (index) => {
    setWorkCountries((rows) => {
      const removed = rows[index];
      if (removed?.id) {
        delete pendingWorkEvidenceByRowIdRef.current[removed.id];
        setPendingFilePickNonce((n) => n + 1);
      }
      if (rows.length <= 1) {
        return [emptyCountryRow()];
      }
      return rows.filter((_, i) => i !== index);
    });
  };

  const downloadProof = useCallback(
    async (row) => {
      const rid = String(row?.id || "").trim();
      const pending = rid ? pendingWorkEvidenceByRowIdRef.current[rid] : null;
      if (pending) {
        triggerBrowserDownload(pending, pending.name);
        return;
      }
      const token = String(authToken || "").trim();
      if (!isApiConfigured() || !token) {
        pushToast("Sign in with the API configured to download saved proof.", "error");
        return;
      }
      if (!(row?.proofFileName || "").trim()) {
        return;
      }
      try {
        const blob = await fetchApplicantWorkEvidenceBlob(rid, token);
        triggerBrowserDownload(blob, row.proofFileName);
      } catch (err) {
        const msg = err instanceof Error ? err.message : "Could not download proof.";
        pushToast(msg, "error");
      }
    },
    [authToken, pushToast]
  );

  const addCustomSocialRow = () => {
    setCustomSocialRows((rows) => {
      if (rows.length >= MAX_CUSTOM_SOCIAL_LINKS) return rows;
      return [...rows, emptyCustomSocialRow()];
    });
  };

  const removeCustomSocialRow = (index) => {
    setCustomSocialRows((rows) => rows.filter((_, i) => i !== index));
  };

  const updateCustomSocialRow = (index, field, value) => {
    setCustomSocialRows((rows) => {
      const next = [...rows];
      if (!next[index]) return rows;
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!isProfileDirty) return;

    const strayDraft = workCountries.some((r) => String(r.countriesInputDraft || "").trim());
    if (strayDraft) {
      pushToast(
        "Finish adding countries from the list (pick a suggestion) or clear the search field before saving.",
        "error"
      );
      return;
    }

    const proofWithoutCountry = workCountries.some((r) => {
      const rid = String(r?.id || "").trim();
      const hasPending = Boolean(rid && pendingWorkEvidenceByRowIdRef.current[rid]);
      const hasNamedProof = Boolean((r.proofFileName || "").trim());
      const hasCountry = (r.countryCodes || []).length > 0;
      return (hasPending || hasNamedProof) && !hasCountry;
    });
    if (proofWithoutCountry) {
      pushToast("Enter at least one country for each row where you uploaded proof.", "error");
      return;
    }

    const residenceIso = String(residenceCountryIso || "").trim().toUpperCase();
    if (!residenceIso || !ISO_SET.has(residenceIso)) {
      pushToast("Select your country of residence.", "error");
      return;
    }

    let nextPhotoDataUrl = photoPreview;
    let latestApiUser = null;
    /** Set when PATCH /applicants/me/profile returns `data.user` (DB-backed session user). */
    let profilePatchUser = null;
    const token = String(authToken || "").trim();
    const canPersistApplicant = isApiConfigured() && token && currentUser?.role === "applicant";

    const customs = customSocialRows
      .slice(0, MAX_CUSTOM_SOCIAL_LINKS)
      .map(({ id, platformName, url }) => ({
        id,
        platformName: (platformName || "").trim(),
        url: (url || "").trim(),
      }))
      .filter((row) => row.platformName || row.url);

    const socialProfiles = {
      linkedin: socialLinkedin.trim(),
      instagram: socialInstagram.trim(),
      youtube: "",
      customs,
    };

    if (canPersistApplicant) {
      try {
        if (pendingPhotoFileRef.current) {
          const { user } = await postApplicantProfilePhoto(pendingPhotoFileRef.current, token);
          pendingPhotoFileRef.current = null;
          setPendingFilePickNonce((n) => n + 1);
          latestApiUser = user;
          nextPhotoDataUrl = user.photoDataUrl || null;
        } else {
          const hadPersistedPhoto =
            currentUser?.photoDataUrl && !String(currentUser.photoDataUrl).startsWith("data:");
          const removingPersistedPhotoWithoutReplace = !photoPreview && hadPersistedPhoto;
          if (removingPersistedPhotoWithoutReplace) {
            const { user } = await deleteApplicantProfilePhoto(token);
            setPendingFilePickNonce((n) => n + 1);
            latestApiUser = user;
            nextPhotoDataUrl = user.photoDataUrl || null;
          }
        }
        if (pendingCvFileRef.current) {
          const { user } = await postApplicantCv(pendingCvFileRef.current, token);
          pendingCvFileRef.current = null;
          setPendingFilePickNonce((n) => n + 1);
          latestApiUser = user;
          if (user.photoDataUrl) {
            nextPhotoDataUrl = user.photoDataUrl;
          }
        } else {
          const hadPersistedCv = !!(currentUser?.cvFileName || "").trim();
          const removingPersistedCvWithoutReplace = !cvFileName.trim() && hadPersistedCv;
          if (removingPersistedCvWithoutReplace) {
            const { user } = await deleteApplicantCv(token);
            setPendingFilePickNonce((n) => n + 1);
            latestApiUser = user;
            if (user.photoDataUrl) {
              nextPhotoDataUrl = user.photoDataUrl;
            }
          }
        }
        const workCountriesPayload = buildWorkCountriesPatchPayload(workCountries);
        const { user: patchProfileUser } = await patchApplicantProfile(
          {
            dateOfBirth: dateOfBirth.trim(),
            workCountries: workCountriesPayload,
            socialProfiles,
            countryOfResidence: residenceIso,
            townCityOfResidence: townCityOfResidence.trim(),
          },
          token
        );
        profilePatchUser = patchProfileUser;
        if (profilePatchUser) {
          latestApiUser = latestApiUser ? { ...latestApiUser, ...profilePatchUser } : profilePatchUser;
        }
        const pendingEvidence = { ...pendingWorkEvidenceByRowIdRef.current };
        for (const [rowId, file] of Object.entries(pendingEvidence)) {
          if (!file) continue;
          const { user: evUser } = await postApplicantWorkEvidence(file, rowId, token);
          delete pendingWorkEvidenceByRowIdRef.current[rowId];
          latestApiUser = evUser;
        }
        if (Object.keys(pendingEvidence).length) {
          setPendingFilePickNonce((n) => n + 1);
        }
      } catch (err) {
        const msg = err instanceof Error ? err.message : "Could not save profile files.";
        pushToast(msg, "error");
        return;
      }
    }

    const savedCountriesLocal = workCountries
      .slice(0, MAX_WORK_COUNTRIES)
      .map(({ id, countryCodes, countriesInputDraft, proofFileName }) => ({
        id,
        countryCodes: [...(countryCodes || [])].filter((c) => ISO_SET.has(c)).sort().join(","),
        countriesInputDraft: String(countriesInputDraft || "").trim(),
        proofFileName: (proofFileName || "").trim(),
      }))
      .filter((row) => row.countryCodes || row.countriesInputDraft || row.proofFileName);

    const wcRawFromApi =
      profilePatchUser != null && latestApiUser && Array.isArray(latestApiUser.workCountries)
        ? latestApiUser.workCountries
        : profilePatchUser != null && Array.isArray(profilePatchUser.workCountries)
          ? profilePatchUser.workCountries
          : null;
    const savedCountriesForState =
      wcRawFromApi != null ? normalizeWorkCountriesFromUser({ workCountries: wcRawFromApi }) : savedCountriesLocal;

    setPhotoPreview(nextPhotoDataUrl || null);

    const nextCvName = (latestApiUser?.cvFileName ?? cvFileName.trim()) || undefined;

    setCurrentUser((prev) => ({
      ...prev,
      ...(latestApiUser || {}),
      name: name.trim(),
      email: email.trim(),
      countryCode: getDialCodeForCountryIso(mobileCountryIso),
      mobile: mobile.trim(),
      dateOfBirth: dateOfBirth.trim(),
      countryOfResidence: residenceIso,
      townCityOfResidence: townCityOfResidence.trim(),
      workCountries: savedCountriesForState,
      locations: undefined,
      photoDataUrl: nextPhotoDataUrl || undefined,
      cvFileName: nextCvName,
      socialProfiles,
    }));
    if (canPersistApplicant) {
      pushToast("Profile saved.", "success");
    } else {
      pushToast(
        "Profile updated in this browser only. Use a configured API URL and stay signed in as an applicant to save to the server.",
        "error"
      );
    }
  };

  const displayPhoto = photoPreview || profilePlaceholder;

  return (
    <ApplicantShell currentUser={currentUser} setCurrentUser={setCurrentUser} authToken={authToken}>
      <main className="nd1-shell nd1-applicant-profile-main jw-applicant-main applicant-home profile-manage-page applicant-profile-page">
        <div className="applicant-profile-shell nd1-applicant-profile-shell">
          <header className="applicant-profile-masthead nd1-applicant-profile-masthead">
            <Link className="back-link nd1-applicant-profile-back" to="/applicant-home">
              &larr; Back to applicant home
            </Link>
            <p className="eyebrow nd1-label nd1-applicant-profile-eyebrow">Your account</p>
            <h1>Manage profile</h1>
          </header>

          <div className="applicant-profile-content profile-manage-card nd1-applicant-profile-content">
          <form
            id="applicant-profile-manage-form"
            className="profile-manage-form applicant-profile-form"
            onSubmit={handleSubmit}
          >
            <div className="applicant-profile-stack">
              <div className="nd1-applicant-profile-brand-row">
              <div className="applicant-profile-surface nd1-applicant-profile-brand-card">
              <div className="profile-manage-photo-column">
                <p className="profile-manage-section-title">Profile photo</p>
                <div className="profile-manage-photo-panel">
                  <div className="profile-manage-photo-panel-body">
                    <div className="profile-manage-photo-preview-wrap">
                      <img
                        src={displayPhoto}
                        alt=""
                        className={`profile-manage-photo-preview${photoPreview ? "" : " profile-manage-photo-preview--placeholder"}`}
                        width={160}
                        height={160}
                      />
                      {photoPreview ? (
                        <button
                          type="button"
                          className="nd1-applicant-profile-preview-delete-btn"
                          onClick={handleProfilePhotoDelete}
                          aria-label="Remove profile photo"
                        >
                          <Trash2 size={16} strokeWidth={1.75} aria-hidden="true" />
                        </button>
                      ) : null}
                    </div>
                  </div>
                  <div className="profile-manage-photo-panel-footer">
                    <input
                      ref={photoInputRef}
                      type="file"
                      accept="image/*"
                      className="profile-manage-file-input"
                      onChange={onPhotoChange}
                      aria-label="Choose profile photo"
                    />
                    <button
                      type="button"
                      className="small-outline-button profile-manage-panel-action-btn nd1-applicant-profile-upload-btn"
                      onClick={() => photoInputRef.current?.click()}
                    >
                      <Upload size={16} strokeWidth={1.75} aria-hidden="true" />
                      {photoPreview ? "Change photo" : "Upload photo"}
                    </button>
                  </div>
                  <p className="profile-manage-helper profile-manage-photo-limit-hint">
                    JPEG, PNG, or WebP — up to {PROFILE_PHOTO_MAX_MB} MB.
                  </p>
                </div>
              </div>
              </div>

              <div className="applicant-profile-surface nd1-applicant-profile-brand-card">
              <div className="profile-manage-cv-column">
                <p className="profile-manage-section-title">CV</p>
                <input
                  ref={cvInputRef}
                  type="file"
                  accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                  className="profile-manage-file-input"
                  onChange={onCvChange}
                  aria-label="Upload CV file"
                />
                {cvFileName.trim() ? (
                  <div className="profile-manage-cv-panel profile-manage-cv-panel--has-file">
                    <div className="profile-manage-cv-panel-body">
                      <div className="profile-manage-cv-has-file-wrap nd1-applicant-profile-cv-file-wrap">
                        <svg
                          className="profile-manage-cv-file-icon nd1-applicant-profile-cv-file-icon"
                          width="40"
                          height="40"
                          viewBox="0 0 24 24"
                          fill="none"
                          aria-hidden="true"
                        >
                          <path
                            d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          <path
                            d="M14 2v6h6M8 13h8M8 17h6M8 9h4"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                        <span
                          className="profile-manage-cv-filename nd1-applicant-profile-cv-filename"
                          title={cvFileName}
                        >
                          {cvFileName}
                        </span>
                        <button
                          type="button"
                          className="nd1-applicant-profile-preview-delete-btn nd1-applicant-profile-cv-delete-btn"
                          onClick={handleCvDelete}
                          aria-label="Remove CV"
                        >
                          <Trash2 size={16} strokeWidth={1.75} aria-hidden="true" />
                        </button>
                      </div>
                    </div>
                    <div className="profile-manage-cv-panel-footer">
                      <button
                        type="button"
                        className="small-outline-button profile-manage-panel-action-btn nd1-applicant-profile-upload-btn"
                        onClick={() => cvInputRef.current?.click()}
                      >
                        <Upload size={16} strokeWidth={1.75} aria-hidden="true" />
                        Change CV
                      </button>
                    </div>
                    <p className="profile-manage-helper profile-manage-cv-limit-hint">
                      PDF, DOC, or DOCX — up to {PROFILE_PHOTO_MAX_MB} MB.
                    </p>
                  </div>
                ) : (
                  <div className="profile-manage-cv-panel profile-manage-cv-panel--empty">
                    <div className="profile-manage-cv-panel-body">
                      <div className="profile-manage-cv-empty-icon-wrap" aria-hidden="true">
                        <svg
                          className="profile-manage-cv-empty-icon"
                          width="40"
                          height="40"
                          viewBox="0 0 24 24"
                          fill="none"
                        >
                          <path
                            d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          <path
                            d="M14 2v6h6"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          <path
                            d="M8 12h8M8 16h6"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeDasharray="2.5 3.5"
                            opacity="0.85"
                          />
                        </svg>
                      </div>
                      <p className="profile-manage-cv-empty-label">No CV uploaded</p>
                    </div>
                    <div className="profile-manage-cv-panel-footer">
                      <button
                        type="button"
                        className="small-outline-button profile-manage-panel-action-btn nd1-applicant-profile-upload-btn"
                        onClick={() => cvInputRef.current?.click()}
                      >
                        <Upload size={16} strokeWidth={1.75} aria-hidden="true" />
                        Upload CV
                      </button>
                    </div>
                    <p className="profile-manage-helper profile-manage-cv-limit-hint">
                      PDF, DOC, or DOCX — up to {PROFILE_PHOTO_MAX_MB} MB.
                    </p>
                  </div>
                )}
              </div>
              </div>
              </div>

              <div className="applicant-profile-surface">
                <p className="profile-manage-section-title applicant-profile-surface-heading nd1-applicant-profile-section-heading">
                  <User className="nd1-applicant-profile-section-icon" strokeWidth={1.75} aria-hidden="true" />
                  Personal details
                </p>

            <div className="profile-manage-form-field">
              <label htmlFor="profileName">Full name</label>
              <input
                id="profileName"
                name="profileName"
                type="text"
                autoComplete="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
              />
            </div>

            <div className="profile-manage-form-field">
              <label htmlFor="profileDob">Date of birth</label>
              <DatePicker
                id="profileDob"
                name="profileDob"
                autoComplete="bday"
                selected={dobSelected}
                onChange={(date) => setDateOfBirth(toYyyyMmDdLocal(date))}
                dateFormat="dd/MM/yyyy"
                placeholderText="dd/mm/yyyy"
                maxDate={new Date()}
                minDate={new Date(1900, 0, 1)}
                showMonthDropdown
                showYearDropdown
                dropdownMode="select"
                scrollableYearDropdown
                yearDropdownItemNumber={100}
                wrapperClassName="profile-manage-dob-picker"
                popperClassName="jobuwin-datepicker-popper"
                popperPlacement="bottom-start"
              />
            </div>

            <div className="profile-manage-residence-row">
              <div className="profile-manage-residence-field">
                <label htmlFor="profileResidenceCountry">Country of residence</label>
                <select
                  id="profileResidenceCountry"
                  name="profileResidenceCountry"
                  autoComplete="country"
                  required
                  value={residenceCountryIso}
                  onChange={(e) => setResidenceCountryIso(e.target.value)}
                >
                  <option value="">Select country</option>
                  {COUNTRIES.map((c) => (
                    <option key={c.value} value={c.value}>
                      {c.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="profile-manage-residence-field">
                <label htmlFor="profileResidenceTown">
                  Town / city of residence{" "}
                  <span className="profile-manage-optional-label">(optional)</span>
                </label>
                <input
                  id="profileResidenceTown"
                  name="profileResidenceTown"
                  type="text"
                  autoComplete="address-level2"
                  maxLength={200}
                  value={townCityOfResidence}
                  onChange={(e) => setTownCityOfResidence(e.target.value)}
                  placeholder="e.g. Manchester"
                />
              </div>
            </div>
            </div>

            <div className="applicant-profile-surface">
              <p className="profile-manage-section-title applicant-profile-surface-heading nd1-applicant-profile-section-heading">
                <MapPin className="nd1-applicant-profile-section-icon" strokeWidth={1.75} aria-hidden="true" />
                Countries where you can legally work
              </p>
              <p className="profile-manage-helper">
                This is optional and is not shared with anyone. Adding it helps give your profile more credibility.
              </p>
              <div className="profile-manage-country-card">
                <div className="profile-manage-country-list">
                  {workCountries.map((row, index) => {
                    const hasCountry = (row.countryCodes || []).length > 0;
                    const pendingProofFile = Boolean(
                      row.id && pendingWorkEvidenceByRowIdRef.current[row.id]
                    );
                    const hasNamedProof = Boolean((row.proofFileName || "").trim());
                    const hasProof = hasNamedProof || pendingProofFile;
                    const needsProofBeforeCountries = !hasCountry;
                    const countriesFieldDisabled = needsProofBeforeCountries && !hasProof;
                    return (
                    <div key={row.id} className="profile-manage-country-row">
                      <div className="profile-manage-country-group">
                        <button
                          type="button"
                          className="profile-manage-remove-country"
                          onClick={() => removeCountryRow(index)}
                          aria-label="Remove this row including proof and country"
                        >
                          <svg
                            className="profile-manage-remove-country-icon"
                            width="20"
                            height="20"
                            viewBox="0 0 24 24"
                            fill="none"
                            aria-hidden="true"
                          >
                            <path
                              d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2m3 0v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6h14M10 11v6M14 11v6"
                              stroke="currentColor"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </button>
                        <div className="profile-manage-country-line">
                          <div className="profile-manage-country-proof-inline profile-manage-country-field">
                            <label htmlFor={`proof-trigger-${row.id}`}>Proof</label>
                            <span className="profile-manage-sr-only" id={`proof-label-${row.id}`}>
                              Proof for this entry
                            </span>
                            <div className="profile-manage-proof-actions-row">
                              <input
                                ref={(el) => {
                                  if (el) proofInputRefs.current[row.id] = el;
                                  else delete proofInputRefs.current[row.id];
                                }}
                                type="file"
                                accept=".pdf,.png,.jpg,.jpeg,.webp,image/*,application/pdf"
                                className="profile-manage-file-input"
                                aria-labelledby={`proof-label-${row.id}`}
                                onChange={onProofChange(index)}
                              />
                              <button
                                id={`proof-trigger-${row.id}`}
                                type="button"
                                className="small-outline-button profile-manage-panel-action-btn profile-manage-country-proof-btn"
                                aria-label={
                                  hasNamedProof || pendingProofFile
                                    ? "Replace proof file"
                                    : "Upload proof file"
                                }
                                onClick={() => {
                                  proofInputRefs.current[row.id]?.click();
                                }}
                              >
                                {hasNamedProof || pendingProofFile ? "Replace" : "Upload"}
                              </button>
                              {hasProof ? (
                                <button
                                  type="button"
                                  className="profile-manage-proof-see-link"
                                  title={
                                    (row.proofFileName || "").trim() ||
                                    (row.id && pendingWorkEvidenceByRowIdRef.current[row.id]?.name) ||
                                    "Download proof"
                                  }
                                  aria-label="Download proof file"
                                  onClick={() => downloadProof(row)}
                                >
                                  See proof
                                </button>
                              ) : (
                                <span className="profile-manage-proof-no-file">No proof provided</span>
                              )}
                            </div>
                          </div>
                          <div className="profile-manage-country-select-group profile-manage-country-field">
                            <label className="profile-manage-country-pills-field-label" htmlFor={`profile-countries-${row.id}`}>
                              Countries
                            </label>
                            <WorkCountriesPillPicker
                              disabled={countriesFieldDisabled}
                              countryCodes={row.countryCodes || []}
                              inputDraft={row.countriesInputDraft || ""}
                              onDraftChange={(v) => updateWorkCountryDraft(index, v)}
                              onAddCode={(iso) => addWorkCountryCode(index, iso)}
                              onRemoveCode={(iso) => removeWorkCountryCode(index, iso)}
                              maxCodes={MAX_COUNTRY_CODES_PER_ROW}
                              inputId={`profile-countries-${row.id}`}
                            />
                            {APPLICANT_COUNTRY_GROUPS.length > 0 ? (
                              <div
                                className="profile-manage-country-group-shortcuts"
                                role="group"
                                aria-label="Add all countries from a predefined group"
                              >
                                {APPLICANT_COUNTRY_GROUPS.map((g) => (
                                  <button
                                    key={g.id}
                                    type="button"
                                    className="profile-manage-country-group-shortcut-link"
                                    disabled={countriesFieldDisabled}
                                    title={`Add: ${g.countryCodes.join(", ")}`}
                                    onClick={() => addWorkCountryGroup(index, g.countryCodes)}
                                  >
                                    <span className="profile-manage-country-group-shortcut-link__plus" aria-hidden="true">
                                      +
                                    </span>
                                    <span className="profile-manage-country-group-shortcut-link__label">{g.label}</span>
                                  </button>
                                ))}
                              </div>
                            ) : null}
                          </div>
                        </div>
                      </div>
                    </div>
                    );
                  })}
                </div>
                <div className="profile-manage-add-country-inner">
                  <p className="profile-manage-country-counter">
                    {workCountries.length}/{MAX_WORK_COUNTRIES}
                  </p>
                  <button
                    type="button"
                    className="small-outline-button profile-manage-add-country profile-manage-icon-plus-btn"
                    onClick={addCountryRow}
                    aria-label="Add another location"
                    disabled={workCountries.length >= MAX_WORK_COUNTRIES}
                  >
                    +
                  </button>
                </div>
              </div>
              </div>

            <div className="applicant-profile-surface">
              <p className="profile-manage-section-title applicant-profile-surface-heading nd1-applicant-profile-section-heading">
                <Lock className="nd1-applicant-profile-section-icon" strokeWidth={1.75} aria-hidden="true" />
                Sign-in &amp; security
              </p>
            <div className="profile-manage-form-field">
              <label htmlFor="profileMobile">Mobile number</label>
              <div className="mobile-row">
              <select
                id="profileMobileCountry"
                name="profileMobileCountry"
                className="profile-manage-field--locked"
                value={mobileCountryIso}
                disabled
                aria-label="Country calling code (cannot be changed here)"
                title="Mobile number cannot be changed on this page"
              >
                {COUNTRY_DIAL_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
              <input
                id="profileMobile"
                name="profileMobile"
                className="profile-manage-field--locked"
                type="tel"
                autoComplete="tel"
                value={mobile}
                placeholder="7123 456789"
                disabled
                title="Mobile number cannot be changed on this page"
              />
            </div>
            </div>

            <div className="profile-manage-form-field">
              <label htmlFor="profileEmail">Email address</label>
              <input
                id="profileEmail"
                name="profileEmail"
                className="profile-manage-field--locked"
                type="email"
                autoComplete="email"
                value={email}
                disabled
                title="Email cannot be changed on this page"
              />
            </div>

            <div className="profile-manage-reset-password-wrap nd1-applicant-profile-account-actions">
              <button
                type="button"
                className="small-outline-button profile-manage-reset-password-btn nd1-applicant-profile-account-btn"
                onClick={() =>
                  navigate("/signin", {
                    state: { passwordReset: true, emailHint: email.trim() || undefined },
                  })
                }
              >
                <Lock size={16} strokeWidth={1.75} aria-hidden="true" />
                Reset password
              </button>
              <button
                type="button"
                className="small-outline-button profile-manage-reset-password-btn nd1-applicant-profile-account-btn"
                disabled={
                  emailChangeBusy || Boolean(emailChangeEligibility && !emailChangeEligibility.canRequest)
                }
                onClick={handleRequestEmailChange}
              >
                <Mail size={16} strokeWidth={1.75} aria-hidden="true" />
                {emailChangeBusy ? "Sending…" : "Change email"}
              </button>
              <button
                type="button"
                className="small-outline-button profile-manage-reset-password-btn nd1-applicant-profile-account-btn"
                disabled={
                  mobileChangeBusy || Boolean(mobileChangeEligibility && !mobileChangeEligibility.canRequest)
                }
                onClick={handleRequestMobileChange}
              >
                <Smartphone size={16} strokeWidth={1.75} aria-hidden="true" />
                {mobileChangeBusy ? "Sending…" : "Change mobile"}
              </button>
            </div>
            {emailChangeHintBlock || mobileChangeHintBlock ? (
              <div className="profile-manage-email-change-hint-wrap profile-manage-account-change-hint-wrap">
                {emailChangeHintBlock}
                {mobileChangeHintBlock}
              </div>
            ) : null}
            </div>

            <div className="applicant-profile-surface applicant-profile-surface--social">
            <div className="profile-manage-social-section">
              <p className="profile-manage-section-title applicant-profile-surface-heading nd1-applicant-profile-section-heading">
                <Link2 className="nd1-applicant-profile-section-icon" strokeWidth={1.75} aria-hidden="true" />
                Social profiles
              </p>
              <p className="profile-manage-helper">
                Optional public links. Only add profiles you are happy for employers to see.
              </p>

              <div className="profile-manage-social-row profile-manage-social-row--brand">
                <SocialBrandIcon network="linkedin" href={socialUrlForLink(socialLinkedin)} />
                <label htmlFor="profileSocialLinkedin">LinkedIn</label>
                <div className="profile-manage-url-field">
                  <span className="profile-manage-url-prefix">https://</span>
                  <input
                    id="profileSocialLinkedin"
                    name="profileSocialLinkedin"
                    type="text"
                    inputMode="url"
                    autoComplete="url"
                    placeholder="www.linkedin.com/in/your-profile"
                    value={socialLinkedin}
                    onChange={(e) => setSocialLinkedin(stripHttpScheme(e.target.value))}
                  />
                </div>
              </div>

              <div className="profile-manage-social-row profile-manage-social-row--brand">
                <SocialBrandIcon network="instagram" href={socialUrlForLink(socialInstagram)} />
                <label htmlFor="profileSocialInstagram">Instagram</label>
                <div className="profile-manage-url-field">
                  <span className="profile-manage-url-prefix">https://</span>
                  <input
                    id="profileSocialInstagram"
                    name="profileSocialInstagram"
                    type="text"
                    inputMode="url"
                    autoComplete="url"
                    placeholder="www.instagram.com/yourusername"
                    value={socialInstagram}
                    onChange={(e) => setSocialInstagram(stripHttpScheme(e.target.value))}
                  />
                </div>
              </div>

              {customSocialRows.map((row, index) => (
                <div key={row.id} className="profile-manage-social-custom-entry">
                  <div className="profile-manage-social-custom-grid">
                    <label className="social-custom-name-label" htmlFor={`profile-social-name-${row.id}`}>
                      Platform name ({index + 1}/{MAX_CUSTOM_SOCIAL_LINKS})
                    </label>
                    <input
                      id={`profile-social-name-${row.id}`}
                      className="social-custom-name-input"
                      type="text"
                      name={`profileSocialCustomName-${row.id}`}
                      autoComplete="off"
                      placeholder="e.g. X, GitHub, Behance"
                      value={row.platformName}
                      onChange={(e) => updateCustomSocialRow(index, "platformName", e.target.value)}
                    />
                    <button
                      type="button"
                      className="profile-manage-social-delete"
                      onClick={() => removeCustomSocialRow(index)}
                      aria-label="Remove this custom profile"
                    >
                      <svg
                        className="profile-manage-social-delete-icon"
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                        fill="none"
                        aria-hidden="true"
                      >
                        <path
                          d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2m3 0v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6h14M10 11v6M14 11v6"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </button>
                    <label className="social-custom-url-label" htmlFor={`profile-social-url-${row.id}`}>
                      Link
                    </label>
                    <div className="social-custom-url-input profile-manage-url-field">
                      <span className="profile-manage-url-prefix">https://</span>
                      <input
                        id={`profile-social-url-${row.id}`}
                        type="text"
                        inputMode="url"
                        autoComplete="url"
                        name={`profileSocialCustomUrl-${row.id}`}
                        placeholder="your-link.com"
                        value={row.url}
                        onChange={(e) =>
                          updateCustomSocialRow(index, "url", stripHttpScheme(e.target.value))
                        }
                      />
                    </div>
                  </div>
                </div>
              ))}

              <div className="profile-manage-add-custom-social-wrap nd1-applicant-profile-add-social-wrap">
                <button
                  type="button"
                  className="nd1-applicant-profile-add-social-btn"
                  onClick={addCustomSocialRow}
                  aria-label="Add another social profile link"
                  disabled={customSocialRows.length >= MAX_CUSTOM_SOCIAL_LINKS}
                >
                  <Plus size={16} strokeWidth={1.75} aria-hidden="true" />
                  Add another link
                </button>
                <p className="profile-manage-custom-social-counter nd1-applicant-profile-add-social-counter">
                  {customSocialRows.length}/{MAX_CUSTOM_SOCIAL_LINKS}
                </p>
              </div>
            </div>
            </div>

            </div>

          </form>

          <div
            className={`job-actions-float nd1-applicant-profile-footer${isProfileDirty ? " job-actions-float--unsaved" : ""}`}
            role="toolbar"
            aria-label={isProfileDirty ? "Profile save actions, unsaved changes" : "Profile save actions"}
          >
            {isProfileDirty ? (
              <p className="profile-manage-unsaved-hint" role="status">
                Changes are not saved.
              </p>
            ) : null}
            <div className="job-actions-float-row">
              <div className="job-actions-buttons">
                <button
                  type="submit"
                  form="applicant-profile-manage-form"
                  className="job-action-btn primary"
                  disabled={!isProfileDirty}
                >
                  Save changes
                </button>
                <button
                  type="button"
                  className="job-action-btn"
                  disabled={!isProfileDirty}
                  aria-label="Discard unsaved changes and restore last saved profile"
                  onClick={resetProfileFormToSaved}
                >
                  Reset
                </button>
                <button type="button" className="job-action-btn" onClick={() => navigate("/applicant-home")}>
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
        </div>
      </main>

      {toast ? (
        <div
          className={`profile-save-toast profile-save-toast--${toast.tone}`}
          role={toast.tone === "error" ? "alert" : "status"}
        >
          <p className="profile-save-toast__message">{toast.message}</p>
          <button type="button" className="profile-save-toast__close" onClick={dismissToast}>
            Close
          </button>
        </div>
      ) : null}
    </ApplicantShell>
  );
}

export default ApplicantProfilePage;
